import React, { memo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Label, InputCalendar, InputTimePicker, Error } from 'app/components';
import { getPostValidationError, getPreValidationError } from 'app/utils';
import useUtils from 'app/hooks/useUtils';
import { FieldRenderProps } from '../DataInputs';

const propTypes = {
  form: PropTypes.objectOf(PropTypes.any).isRequired,
  errors: PropTypes.arrayOf(PropTypes.object),
  dataFields: PropTypes.shape({
    preferredDate: PropTypes.shape(FieldRenderProps),
    timeFrom: PropTypes.shape(FieldRenderProps),
    timeTo: PropTypes.shape(FieldRenderProps)
  }).isRequired,
  submitFailed: PropTypes.bool.isRequired,
};

const defaultProps = {
  errors: []
};

const dateOptions = {
  year: 'numeric',
  month: 'short',
  day: 'numeric'
};

const options = {
  hour: 'numeric',
  minute: 'numeric'
};

const PreferredDate = ({ form, errors, submitFailed, dataFields: { preferredDate, timeFrom, timeTo } }) => {
  const [t, i18n] = useTranslation();
  const selectedLang = i18n.language === 'en-US' ? 'en' : i18n.language === 'de-DE' ? 'de' : i18n.language === 'de' ? 'de' : i18n.language === 'en' && 'en';
  const { getDateWithOffset } = useUtils();
  const maxdate = new Date();
  const minDate = new Date();
  maxdate.setFullYear(maxdate.getFullYear() + 14);

  return (
    <div className="cinema-rent__preferred-date-wrapper">
      <div>
        <h2>{t('cinema_rent_inquiry_form.Preferred_date')}</h2>
        <div className="cinema-rent__preferred-date-input">
          <Label>{t('cinema_rent_inquiry_form.Preferred_date')} *</Label>
          <InputCalendar
            {...preferredDate.input}
            placeholder={t('common.choose')}
            id={preferredDate.input.name}
            minDate={minDate}
            maxDate={maxdate}
            value={preferredDate.input.value || getDateWithOffset()}
            inputValue={
              preferredDate.input.value &&
              (selectedLang === 'en'
                ? new Intl.DateTimeFormat('en-US', dateOptions).format(preferredDate.input.value)
                : new Intl.DateTimeFormat(`${i18n.language}`, dateOptions).format(preferredDate.input.value))}
            error={
              getPreValidationError(preferredDate, t) ||
              getPostValidationError(errors, preferredDate.input.name, t)
            }
            onFocus={preferredDate.input.onFocus}
            onBlur={preferredDate.input.onBlur}
            onChange={value => {
              form.change(preferredDate.input.name, value);
            }}
          />
          {submitFailed && (preferredDate.meta?.error || !Array.from(preferredDate.input.value).includes('')) && (
            <Error>
              {/* {(preferredDate.meta?.error && t(preferredDate.meta?.error)) || t('common.select_value_error')} */}
              {(preferredDate.meta?.error && t(preferredDate.meta?.error))}
            </Error>
          )}
        </div>
      </div>
      <div>
        <h2>{t('cinema_rent_inquiry_form.time_frame')}</h2>
        <div className="cinema-rent__preferred-date-time-frame">
          <div className="cinema-rent__preferred-date-input">
            <Label>{t('cinema_rent_inquiry_form.time_from')} *</Label>
            <InputTimePicker
              {...timeFrom.input}
              placeholder={t('common.choose')}
              id={timeFrom.input.name}
              value={timeFrom.input.value || new Date()}
              inputValue={
                timeFrom.input.value &&
                (selectedLang === 'en'
                  ? new Intl.DateTimeFormat('en-US', options).format(timeFrom.input.value)
                  : new Intl.DateTimeFormat('de-DE', options).format(timeFrom.input.value))
              }
              error={
                getPreValidationError(timeFrom, t) ||
                getPostValidationError(errors, timeFrom.input.name, t)
              }
              onFocus={timeFrom.input.onFocus}
              onBlur={timeFrom.input.onBlur}
              onChange={value => {
                form.change(timeFrom.input.name, value);
              }}
            />
            {submitFailed && (timeFrom.meta?.error || !Array.from(timeFrom.input.value).includes('')) && (
              <Error>
                {/* {(timeFrom.meta?.error && t(timeFrom.meta?.error)) || t('common.select_value_error')} */}
                {(timeFrom.meta?.error && t(timeFrom.meta?.error))}
              </Error>
            )}
          </div>
          <div className="cinema-rent__preferred-date-input">
            <Label>{t('cinema_rent_inquiry_form.time_until')} *</Label>
            <InputTimePicker
              {...timeTo.input}
              placeholder={t('common.choose')}
              id={timeTo.input.name}
              value={timeTo.input.value || new Date()}
              inputValue={
                timeTo.input.value &&
                (selectedLang === 'en'
                  ? new Intl.DateTimeFormat('en-US', options).format(timeTo.input.value)
                  : new Intl.DateTimeFormat('de-DE', options).format(timeTo.input.value))
              }
              error={
                getPreValidationError(timeTo, t) ||
                getPostValidationError(errors, timeTo.input.name, t)
              }
              onFocus={timeTo.input.onFocus}
              onBlur={timeTo.input.onBlur}
              onChange={value => {
                form.change(timeTo.input.name, value);
              }}
            />
            {submitFailed && (timeTo.meta?.error || !Array.from(timeTo.input.value).includes('')) && (
              <Error>
                {/* {(timeTo.meta?.error && t(timeTo.meta?.error)) || t('common.select_value_error')} */}
                {(timeTo.meta?.error && t(timeTo.meta?.error))}
              </Error>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

PreferredDate.propTypes = propTypes;
PreferredDate.defaultProps = defaultProps;

export default memo(PreferredDate);
